import React, { useState, useEffect } from 'react';
import DataTable from "../../../components/DataTable";
import data from "./data/dataTableData"
import theme from "../../../assets/theme";
import ExpandableComponent from "./components/ExpandableComponent";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import HeaderBar, {ReturnFilters} from "./components/HeaderBar";
import { ReturnOrderController } from "../../../controllers/returnOrderController";

interface ReturnsDataType {
    data: any[];
    totalPages: number;
    totalElements: number;
    last: boolean;
    first: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
    numberOfElements: number;
}

interface ApiResponse {
    status: number;
    message: string | null;
    errors: any | null;
    data: ReturnsDataType;
    timeStamp: number;
}

function Returns() {
    const {typography, palette} = theme;
    const [returnsData, setReturnsData] = useState<ReturnsDataType>({
        data: [],
        totalPages: 0,
        totalElements: 0,
        last: true,
        first: true,
        hasNext: false,
        hasPrevious: false,
        numberOfElements: 0
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [filters, setFilters] = useState<ReturnFilters>({});

    const fetchReturnsData = async () => {
        setLoading(true);
        try {
            // getReturns hits /orders/returns, which doesn't exist on the backend
            // — getMerchantReturnedItems calls /orders/merchant/returned-items
            // (the ROLE_MERCHANT endpoint), which is what this screen needs.
            const response = await ReturnOrderController.getMerchantReturnedItems({ page, size: pageSize });
            const apiResponse = response.data as ApiResponse;
            if (apiResponse && apiResponse.data) {
                setReturnsData(apiResponse.data);
            }
        } catch (error) {
            console.error("Error fetching returns data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReturnsData();
    }, [page, pageSize]);

    // Build the table from the live API rows (product, qty, reason, date,
    // price) instead of the static placeholder data that used to render here.
    const tableData = {
        columns: data.columns,
        rows: (returnsData.data || []).filter((r: any) => {
            const search = (filters.search ?? '').trim().toLowerCase();
            const matchesSearch = !search
                || String(r.productName ?? '').toLowerCase().includes(search)
                || String(r.returnReason ?? '').toLowerCase().includes(search);
            const matchesStatus = !filters.status || (r.approvalStatus || 'REQUESTED') === filters.status;
            return matchesSearch && matchesStatus;
        }).map((r: any) => ({
            productName: [r.productName ?? '—'],
            qty: r.quantity ?? 0,
            reason: r.returnReason ?? '—',
            date: r.orderDate ? String(r.orderDate).slice(0, 10) : '—',
            price: r.amount != null ? `₹${r.amount}` : '—',
            details: {
                id: r.id,
                reason: r.returnReason ?? '—',
                amount: r.amount,
                subOrderId: r.subOrderId,
                productName: r.productName ?? '—',
                qty: r.quantity ?? 0,
                date: r.orderDate ? String(r.orderDate).slice(0, 10) : '—',
                // Returns created before the approval gate have no status -> REQUESTED.
                approvalStatus: r.approvalStatus || 'REQUESTED',
                rejectionReason: r.rejectionReason,
                returnImages: r.returnImages || [],
                onRefresh: fetchReturnsData,
            },
        })),
    };

    return (
        <>
            <FaiBox className={"returns"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Returns
                </FaiTypography>
                <FaiBox className={"returns-content"}>
                    <FaiBox className={"returns-content-header"}>
                        <HeaderBar filters={filters} onFilterChange={setFilters}/>
                    </FaiBox>
                    <FaiBox className={"returns-content-body"} mt={3}>
                        {loading ? (
                            <FaiTypography>Loading...</FaiTypography>
                        ) : returnsData.numberOfElements === 0 ? (
                            <FaiBox sx={{ textAlign: 'center', padding: '20px' }}>
                                <FaiTypography variant="body1">No returns data available.</FaiTypography>
                            </FaiBox>
                        ) : (
                            <DataTable
                                showHeader={false}
                                table={tableData}
                                expandRows={true}
                                renderRowSubComponent={ExpandableComponent}
                                expandedRowObj={tableData}
                                pagination={{
                                    variant: "contained",
                                    color: "primary",
                                }}
                            />
                        )}
                    </FaiBox>
                </FaiBox>
            </FaiBox>
        </>
    );
}

export default Returns;
